import { Link, useLocation } from "react-router-dom";
import { Building2, ExternalLink, BadgeCheck, Rocket } from "lucide-react";
import { Badge } from "@/components/ui";
import { formatDate, formatRelative } from "@/lib/format";
import { PLAN_COLORS, STAGE_BY_KEY, money, isNearTrial } from "@/features/accounts/platform-meta";
import type { Contact } from "@/lib/types";

/**
 * The contact's SyncSuite platform life, as a first-class panel — this is
 * OUR CRM about OUR customers, so their platform accounts, plan, journey and
 * money live right on the contact record instead of hiding in raw
 * platform_* custom fields (which this panel replaces in the UI; the fields
 * remain the storage + automation surface underneath).
 *
 * Data: GET /contacts/:id → platform_accounts (see routes/contacts.ts),
 * fed by the platform → CRM activity pipeline + nightly sync.
 */

export interface ContactPlatformAccount {
  id: string;
  role: string;
  slug: string | null;
  name: string | null;
  plan_code: string | null;
  status: string | null;
  stage: string;
  account_tags: string[];
  platform_created_at: string | null;
  last_active_at: string | null;
  email_verified_at: string | null;
  phone_verified_at: string | null;
  first_project_at: string | null;
  first_published_at: string | null;
  card_added_at: string | null;
  upgraded_at: string | null;
  canceled_at: string | null;
  plan_history: { at: string; from: string | null; to: string | null; reason: string | null }[];
  first_domain_live_at?: string | null;
  auto_topup_enabled?: boolean | null;
  subscription_status?: string | null;
  trial_ends_at?: string | null;
  revenue_usd?: string | null;
  lifetime_spend_usd: string | null;
  wallet_balance_usd: string | null;
}

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

export function SyncSuitePanel({
  contact,
  accounts,
  hideCameFrom,
}: {
  contact: Contact;
  accounts: ContactPlatformAccount[];
  /** The contact page shows "How they found us" in full, so it hides this. */
  hideCameFrom?: boolean;
}) {
  const location = useLocation();
  const cf = (contact.custom_fields ?? {}) as Record<string, unknown>;

  // Signup attribution captured by the marketing site / checkout.
  const attribution: { label: string; value: string }[] = [];
  const utmSource = str(cf.signup_utm_source);
  const utmCampaign = str(cf.signup_utm_campaign);
  if (utmSource) attribution.push({ label: "Source", value: utmCampaign ? `${utmSource} · ${utmCampaign}` : utmSource });
  const referrer = str(cf.signup_referrer);
  if (!utmSource && referrer) {
    try { attribution.push({ label: "Referrer", value: new URL(referrer).hostname }); } catch { attribution.push({ label: "Referrer", value: referrer }); }
  }
  const landing = str(cf.signup_landing_page);
  if (landing) attribution.push({ label: "Landed on", value: landing });
  const affiliate = str(cf.signup_affiliate_code);
  if (affiliate) attribution.push({ label: "Referral code", value: affiliate });
  const country = str(cf.signup_country);
  const locale = str(cf.signup_locale);
  if (country || locale) {
    attribution.push({ label: "Region", value: [country, locale].filter(Boolean).join(" · ") });
  }
  // Referral linkage (filled by the platform sync from affiliate attributions).
  const referredById = str(cf.platform_referred_by_contact_id);
  const referredByName = str(cf.platform_referred_by_name);
  const referralsMade =
    typeof cf.platform_referrals_count === "number" ? cf.platform_referrals_count : 0;

  const daysToUpgrade =
    typeof cf.platform_days_to_upgrade === "number" ? cf.platform_days_to_upgrade : null;
  const spendTotal = accounts.reduce(
    (n, a) => n + (Number(a.lifetime_spend_usd) || 0) + (Number(a.revenue_usd) || 0), 0);

  if (accounts.length === 0 && attribution.length === 0 && !referredById && referralsMade === 0) return null;

  return (
    <div className="cc-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
          <Building2 className="h-4 w-4 text-indigo-300" /> Their SyncSuite
        </h3>
        {spendTotal > 0 && (
          <span className="text-xs font-semibold tabular-nums text-emerald-300">
            ${spendTotal.toFixed(2)} lifetime
          </span>
        )}
      </div>

      {/* Headline facts about the person's platform relationship. */}
      <div className="mb-3 flex flex-wrap gap-1">
        {accounts.some((a) => a.email_verified_at) && (
          <Badge color="cyan" size="xs"><BadgeCheck className="mr-0.5 h-3 w-3" /> Email verified</Badge>
        )}
        {accounts.some((a) => a.phone_verified_at) && (
          <Badge color="cyan" size="xs"><BadgeCheck className="mr-0.5 h-3 w-3" /> Phone verified</Badge>
        )}
        {daysToUpgrade != null && (
          <Badge color="violet" size="xs">
            <Rocket className="mr-0.5 h-3 w-3" />
            {daysToUpgrade === 0 ? "Upgraded on day one" : `Upgraded in ${daysToUpgrade}d`}
          </Badge>
        )}
        {referralsMade > 0 && (
          <Badge color="green" size="xs">
            {referralsMade === 1 ? "1 referral" : `${referralsMade} referrals`}
          </Badge>
        )}
      </div>

      {/* One tile per account. */}
      <div className="space-y-2">
        {accounts.map((a) => {
          const stage = STAGE_BY_KEY[a.stage] ?? null;
          const lastHop = a.plan_history?.[a.plan_history.length - 1] ?? null;
          return (
            <Link
              key={a.id}
              to={`/accounts/${a.id}`}
              state={{ from: location.pathname }}
              className="block rounded-lg border border-zinc-800/80 bg-zinc-900/50 p-2.5 hover:border-indigo-500/40 hover:bg-zinc-900/80"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-sm font-medium text-zinc-100">
                  {a.name || a.slug || "Workspace"}
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {a.plan_code && (
                    <Badge color={PLAN_COLORS[a.plan_code] ?? "zinc"} size="xs">{a.plan_code}</Badge>
                  )}
                  {a.plan_code && a.plan_code !== "free" && a.subscription_status === "trialing" && (
                    isNearTrial(a.trial_ends_at)
                      ? <Badge color="amber" size="xs">trial</Badge>
                      : <Badge color="zinc" size="xs">beta</Badge>
                  )}
                  {a.plan_code && a.plan_code !== "free" &&
                    (Number(a.revenue_usd) > 0 || a.subscription_status === "active") && (
                    <Badge color="emerald" size="xs">paying</Badge>
                  )}
                  {stage && stage.label !== "Paying" && (
                    <Badge color={stage.color} size="xs">{stage.label}</Badge>
                  )}
                  <ExternalLink className="h-3 w-3 text-zinc-600" />
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-zinc-500">
                {a.platform_created_at && <span>Joined {formatDate(a.platform_created_at)}</span>}
                {a.upgraded_at && <span className="text-violet-300">Pro since {formatDate(a.upgraded_at)}</span>}
                {lastHop && !a.upgraded_at && lastHop.to && lastHop.from && (
                  <span>{lastHop.from} → {lastHop.to}</span>
                )}
                {money(a.wallet_balance_usd) && (
                  <span title="Balance as of the last nightly platform sync">
                    Wallet {money(a.wallet_balance_usd)}
                  </span>
                )}
                {a.auto_topup_enabled === true && <span className="text-emerald-400">Auto top-up on</span>}
                {a.auto_topup_enabled === false && a.card_added_at && <span className="text-amber-400">Auto top-up off</span>}
                {a.last_active_at && <span>Active {formatRelative(a.last_active_at)}</span>}
              </div>
            </Link>
          );
        })}
      </div>

      {/* Where they came from — first-touch attribution. */}
      {!hideCameFrom && (attribution.length > 0 || referredById || referredByName) && (
        <div className="mt-3 border-t border-zinc-800/60 pt-2.5">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
            Came from
          </div>
          {(referredById || referredByName) && (
            <div className="mb-0.5 flex items-baseline justify-between gap-2 text-[11px]">
              <span className="shrink-0 text-zinc-500">Referred by</span>
              {referredById ? (
                <Link
                  to={`/contacts/${referredById}`}
                  state={{ from: location.pathname }}
                  className="min-w-0 truncate text-right text-indigo-300 hover:text-indigo-200 hover:underline"
                >
                  {referredByName ?? "View referrer"}
                </Link>
              ) : (
                <span className="min-w-0 truncate text-right text-zinc-300">{referredByName}</span>
              )}
            </div>
          )}
          <dl className="space-y-0.5">
            {attribution.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-2 text-[11px]">
                <dt className="shrink-0 text-zinc-500">{row.label}</dt>
                <dd className="min-w-0 truncate text-right text-zinc-300" title={row.value}>{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
